import { useState } from "react";

import { Badge, Button, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";

import { DateTime } from "luxon";

import { ServiceOrderType } from "../../../_constants/serviceOrder.ts";
import ServiceTypeComponent from "../../../common/components/ServiceTypeComponent.tsx";
import ServiceTypeDiagComponent from "../../../common/components/ServiceTypeDiagComponent.tsx";
import history from "../../../routes/history";
import { MY_ORDERS } from "../../orders/routes";
import {
  IDiagnosticServiceOrdering,
  IErpExpressServiceOrdering,
  IInventoryServiceOrdering,
} from "../interfaces";
import { createServices } from "../services";
import { useServiceInventoryStore } from "../store/ServiceInventoryStore";

const ServiceOrderingShoppingCart = () => {
  const [isLoading, setLoading] = useState(false);
  const {
    dwelling,
    inventory,
    erpExpress,
    pppt,
    condominiumVisit,
    condominiumCreation,
    energyAudit,
    diagnostic,
    incomingTenants,
    outgoingTenants,
    tenantLeaseEndDate,
    tenantLeaseStartDate,
    isManagedByMainGroup,
    operator,
    manager,
    getData,
    removeElement,
    reset,
  } = useServiceInventoryStore((state) => state);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  type DiagnosticData = {
    prestation: string;
    serviceOrdering: string;
  };

  type DataUnion =
    | (IInventoryServiceOrdering & DiagnosticData)
    | (IErpExpressServiceOrdering & DiagnosticData)
    | (IDiagnosticServiceOrdering & DiagnosticData);

  const data = (): DataUnion[] => [
    ...(inventory
      ? [
          {
            ...(inventory as IInventoryServiceOrdering),
            prestation: inventory.type,
            serviceOrdering: ServiceOrderType.INVENTORY,
          },
        ]
      : []),
    ...(erpExpress
      ? [
          {
            ...(erpExpress as IErpExpressServiceOrdering),
            prestation: ServiceOrderType.ERP_EXPRESS,
            serviceOrdering: ServiceOrderType.ERP_EXPRESS,
          },
        ]
      : []),
    ...(pppt
      ? [
          {
            ...(pppt as IDiagnosticServiceOrdering),
            prestation: ServiceOrderType.PPPT,
            serviceOrdering: ServiceOrderType.PPPT,
          },
        ]
      : []),
    ...(condominiumVisit
      ? [
          {
            ...(condominiumVisit as IDiagnosticServiceOrdering),
            prestation: ServiceOrderType.CONDOMINIUM_VISIT,
            serviceOrdering: ServiceOrderType.CONDOMINIUM_VISIT,
          },
        ]
      : []),
    ...(condominiumCreation
      ? [
          {
            ...(condominiumCreation as IDiagnosticServiceOrdering),
            prestation: ServiceOrderType.CONDOMINIUM_CREATION,
            serviceOrdering: ServiceOrderType.CONDOMINIUM_CREATION,
          },
        ]
      : []),
    ...(energyAudit
      ? [
          {
            ...(energyAudit as IDiagnosticServiceOrdering),
            prestation: ServiceOrderType.ENERGY_AUDIT,
            serviceOrdering: ServiceOrderType.ENERGY_AUDIT,
          },
        ]
      : []),
    ...(diagnostic
      ? [
          {
            ...(diagnostic as IDiagnosticServiceOrdering),
            prestation: ServiceOrderType.DIAGNOSTIC,
            serviceOrdering: ServiceOrderType.DIAGNOSTIC,
          },
        ]
      : []),
  ];
  const submitHandler = async () => {
    if (!isLoading) {
      try {
        setLoading(true);
        const serviceOrdering = getData();
        const hasErpExpressOrder = data().some(
          (item) => item.serviceOrdering === ServiceOrderType.ERP_EXPRESS
        );

        const response: Array<{ id: string; type: string }> =
          await createServices(serviceOrdering);
        if (!(response.length > 0 && response[0].id)) {
          throw "Error de création";
        }
        if (hasErpExpressOrder) {
          const erpExpressOrder = response.find(
            (order) => order.type === ServiceOrderType.ERP_EXPRESS
          );
          localStorage.setItem("showErpExpressQuoteBanner", "true");
          if (erpExpressOrder) {
            localStorage.setItem("erpExpressOrderId", erpExpressOrder.id);
          }
        }
        reset();
        handleClose();
        toast.success("Commande validée !");
        setTimeout(() => {
          history.push(MY_ORDERS);
        }, 500);
      } catch (e: any) {
        console.log("error createService :", e?.message);
        toast.error(
          "Impossible de valider la commande.\nVeuillez contracter votre administrateur système."
        );
      }
      setLoading(false);
    }
  };
  return (
    <div className="d-flex align-items-center">
      <Button variant="primary" onClick={handleShow}>
        <i className="bi bi-cart4" /> Panier{" "}
        <Badge bg="success">{data().length}</Badge>
      </Button>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-cart4" /> Mon Panier
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {incomingTenants && incomingTenants.length > 0 && (
            <div>
              <div className="serviceOrdering-title d-flex align-items-center gap-3">
                Locataires entrants
                {tenantLeaseStartDate && (
                  <Badge bg="info">
                    Signature du bail le{" "}
                    {DateTime.fromJSDate(
                      new Date(tenantLeaseStartDate)
                    ).toFormat("dd LLL yyyy")}
                  </Badge>
                )}
              </div>
              <Table>
                <tbody>
                  {incomingTenants.map((tenant, index) => (
                    <tr key={index}>
                      <td>{tenant.lastname}</td>
                      <td>{tenant.firstname}</td>
                      <td>{tenant.phone}</td>
                      <td>{tenant.email}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {outgoingTenants && outgoingTenants.length > 0 && (
            <div>
              <div className="serviceOrdering-title d-flex align-items-center gap-3">
                Locataires sortants
                {tenantLeaseEndDate && (
                  <Badge bg="warning">
                    Fin de bail le{" "}
                    {DateTime.fromJSDate(new Date(tenantLeaseEndDate)).toFormat(
                      "dd LLL yyyy"
                    )}
                  </Badge>
                )}
              </div>
              <Table>
                <tbody>
                  {outgoingTenants.map((tenant, index) => (
                    <tr key={index}>
                      <td>{tenant.lastname}</td>
                      <td>{tenant.firstname}</td>
                      <td>{tenant.phone}</td>
                      <td>{tenant.email}</td>
                      <td>
                        {tenant.rentalStartDate && (
                          <Badge bg="primary">
                            Entrée le{" "}
                            {DateTime.fromJSDate(
                              new Date(tenant.rentalStartDate)
                            ).toFormat("dd LLL yyyy")}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {dwelling && (
            <div>
              <div className="serviceOrdering-title">Adresse</div>
              <div className="p-2">
                <div className="my-3">
                  {dwelling.address.address.address}{" "}
                  {dwelling.address.address.zip} {dwelling.address.address.city}
                </div>
              </div>
            </div>
          )}
          {operator ? (
            <div>
              <div className="serviceOrdering-title">Opérateur</div>
              <div className="p-2">
                <div className="my-3">{operator.label}</div>
              </div>
            </div>
          ) : null}
          {manager ? (
            <div>
              <div className="serviceOrdering-title">
                Gestionnaire / commercial
              </div>
              <div className="p-2">
                <div className="my-3">{manager.label}</div>
              </div>
            </div>
          ) : null}
          <div className="serviceOrdering-title">Prestations</div>
          {data().length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <td>Prestation</td>
                  <td>Rendez-vous</td>
                  <td>Action</td>
                </tr>
              </thead>
              <tbody>
                {data().map((item, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        <ServiceTypeComponent
                          className={"m-1"}
                          type={item.prestation as ServiceOrderType}
                        />

                        {(item.prestation === ServiceOrderType.DIAGNOSTIC ||
                          item.prestation === ServiceOrderType.PPPT ||
                          item.prestation ===
                            ServiceOrderType.CONDOMINIUM_VISIT ||
                          item.prestation ===
                            ServiceOrderType.CONDOMINIUM_CREATION ||
                          item.prestation === ServiceOrderType.ENERGY_AUDIT ||
                          item.prestation ===
                            ServiceOrderType.CONDOMINIUM_CREATION) &&
                          (ServiceOrderType.ENERGY_AUDIT ||
                            item.prestation ===
                              ServiceOrderType.CONDOMINIUM_CREATION) && (
                            <ServiceTypeDiagComponent
                              item={item as IDiagnosticServiceOrdering}
                            />
                          )}
                      </td>
                      <td>
                        <Badge bg="success">
                          {!item?.appointment?.startDate && isManagedByMainGroup
                            ? "À fixer par Dwell"
                            : DateTime.fromJSDate(
                                new Date(item.appointment.startDate)
                              ).toFormat("le dd LLL yyyy à HH:mm")}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeElement(item.serviceOrdering)}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <span>Votre panier est vide</span>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Retour
          </Button>
          <Button
            variant="primary"
            disabled={data().length < 1}
            onClick={submitHandler}
          >
            {isLoading ? (
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              "Valider la commande"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ServiceOrderingShoppingCart;
